import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import ConfirmModal from '../../components/portal/common/ConfirmModal';
import { useTranslation } from 'react-i18next';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;
const ContentWrapper = styled.div`
  position: relative; max-width: 1200px; margin: 0 auto; padding: var(--desktop-page-padding-x, 2rem); padding-top: 3rem;
  @media (max-width: 768px) {
    max-width: none;
    margin: 0;  
      padding: 0 var(--mobile-page-padding-x);
    width: 100%;
    padding-top: 4rem;
  }
`;
const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0,0,0,0.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  margin-bottom: 1rem; padding: 1.25rem 1.5rem;
`;

const label = { fontSize: 12, color: '#666', fontWeight: 700, textTransform: 'uppercase' } as const;

const StudentExamDetail: React.FC = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string|null>(null);
  const [toast, setToast] = React.useState<string|null>(null);
  const [exam, setExam] = React.useState<any|null>(null);
  const [studentNote, setStudentNote] = React.useState<string>('');
  const [teacherNote, setTeacherNote] = React.useState<string>('');
  const [savingNote, setSavingNote] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [showCancelModal, setShowCancelModal] = React.useState(false);

  const load = React.useCallback(async ()=>{
    if (!examId) return;
    setLoading(true); setError(null);
    try {
      const resp = await apiService.get<any>(`/api/exams/student/${examId}`);
      const data = (resp && typeof resp === 'object' && 'data' in resp) ? (resp as any).data : resp;
      setExam(data || null);
      
      // Resolve notes
      if (data && Array.isArray(data.notes)) {
        const myNotes = data.notes.filter((n: any) => n.author?.roles?.some((r: any) => r.name === 'ROLE_STUDENT'));
        const tNotes = data.notes.filter((n: any) => n.author?.roles?.some((r: any) => r.name === 'ROLE_TEACHER' || r.name === 'ROLE_SUPER_ADMIN'));
        const myNote = myNotes.length > 0 ? myNotes[myNotes.length - 1] : null;
        const tNote = tNotes.length > 0 ? tNotes[tNotes.length - 1] : null;
        setStudentNote(myNote?.content || '');
        setTeacherNote(tNote?.content || '');
      }
    } catch(e:any){ setError(e?.message || 'Failed to load exam'); }
    finally{ setLoading(false); }
  }, [examId]);

  React.useEffect(()=>{ load(); }, [load]);

  const saveStudentNote = async () => {
    const content = studentNote.trim();
    if (!content) { setError('Note cannot be empty'); return; }
    try {
      setSavingNote(true); setError(null);
      const qs = new URLSearchParams();
      qs.set('content', content);
      const resp = await apiService.post<any>(`/api/exams/${examId}/notes?${qs.toString()}`);
      setToast(resp?.message || 'Note saved');
      setTimeout(()=> setToast(null), 2000);
      await load();
    } catch(e:any){ setError(e?.message || 'Failed to save note'); }
    finally{ setSavingNote(false); }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('nl-NL',{ year:'numeric', month:'long', day:'2-digit', timeZone:'Europe/Amsterdam' }); }
    catch { return new Date(iso).toLocaleDateString('nl-NL',{ year:'numeric', month:'long', day:'2-digit' }); }
  };
  const formatDateTime = (iso?: string) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('nl-NL',{ dateStyle:'medium', timeStyle:'short', timeZone:'Europe/Amsterdam' }); }
    catch { return new Date(iso).toLocaleString('nl-NL',{ dateStyle:'medium', timeStyle:'short' }); }
  };

  return (
    <PageWrapper>
      <ContentWrapper>
        {toast && (
          <div style={{ position:'fixed', top:90, right:24, background:'linear-gradient(135deg,#11998e,#38ef7d)', color:'#fff', padding:'.6rem .9rem', borderRadius:12, boxShadow:'0 10px 30px rgba(0,0,0,0.15)', zIndex:3000, fontWeight:700 }}>✅ {toast}</div>
        )}
        {error && (
          <div style={{ position:'fixed', top:90, right:24, background:'linear-gradient(135deg,#fc4a1a,#f7b733)', color:'#fff', padding:'.6rem .9rem', borderRadius:12, boxShadow:'0 10px 30px rgba(0,0,0,0.15)', zIndex:3000, fontWeight:700 }}>⚠️ {error}</div>
        )}

        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
            <div style={{ fontWeight: 900 }}>Details</div>
            {exam && !exam.result && new Date(exam.startTime || exam.examDate) > new Date() && (
              <button
                onClick={() => setShowCancelModal(true)}
                disabled={cancelling}
                style={{ border: 0, borderRadius: 10, padding: '.35rem .65rem', fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg,#fc4a1a,#f7b733)', fontSize: '0.75rem', cursor: 'pointer' }}
              >
                {cancelling ? t('portal.common.cancelling', 'Cancelling...') : t('portal.student.exams.cancel_button', 'Cancel Exam')}
              </button>
            )}
          </div>
          {loading ? (
            <div style={{ padding:'1rem', color:'#555' }}>Loading…</div>
          ) : !exam ? (
            <div style={{ padding:'1rem', color:'#666' }}>Exam not found.</div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:'.5rem' }}>
              <div>
                <div style={label}>Exam date</div>
                <div style={{ fontWeight:800 }}>{exam.startTime ? formatDate(exam.startTime) : '—'}</div>
              </div>
              <div>
                <div style={label}>Result</div>
                <div style={{ fontWeight:800 }}>
                  {(() => {
                    const now = new Date();
                    const examTime = exam.startTime;
                    const dt = examTime ? new Date(examTime) : null;
                    if (dt && dt > now) return '—';
                    if (exam.result === true) return 'Passed';
                    if (exam.result === false) return 'Not passed';
                    return '—';
                  })()}
                </div>
              </div>
              <div>
                <div style={label}>Place</div>
                <div style={{ fontWeight:800 }}>{exam.location || '—'}</div>
              </div>
              <div>
                <div style={label}>Created at</div>
                <div style={{ fontWeight:800 }}>{exam.createdAt ? formatDateTime(exam.createdAt) : '—'}</div>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <div style={{ fontWeight:900, marginBottom:'.5rem' }}>Notes</div>
          {!exam ? (
            <div style={{ color:'#666' }}>—</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              <div>
                <div style={{ ...label, marginBottom: '0.25rem' }}>Your Note</div>
                <input
                  type="text"
                  value={studentNote}
                  onChange={(e)=> setStudentNote(e.target.value.slice(0,2000))}
                  placeholder={savingNote ? 'Saving...' : 'Enter your note...'}
                  disabled={savingNote}
                  style={{ 
                    width:'100%', 
                    padding:'.75rem', 
                    borderRadius:10, 
                    border:'1px solid rgba(0,0,0,0.12)', 
                    fontSize: '0.9rem',
                    background: '#fff',
                    cursor: 'text'
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      saveStudentNote();
                    }
                  }}
                />
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'.5rem' }}>
                  <div style={{ fontSize:12, color:'#666' }}>{studentNote.length}/2000</div>
                  <button
                    onClick={saveStudentNote}
                    disabled={savingNote || !studentNote.trim()}
                    style={{ border:0, borderRadius:10, padding:'.45rem 1rem', fontWeight:800, color:'#fff', background:'linear-gradient(135deg,#11998e,#38ef7d)', cursor: 'pointer' }}
                  >
                    {savingNote ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <div>
                <div style={{ ...label, marginBottom: '0.25rem' }}>Teacher Note</div>
                <div style={{ 
                  width:'100%', 
                  padding:'.75rem', 
                  borderRadius:10, 
                  border:'1px solid rgba(0,0,0,0.08)', 
                  fontSize: '0.9rem',
                  background: '#f9f9f9',
                  color: '#555',
                  minHeight: '42px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {teacherNote || 'No teacher note yet.'}
                </div>
              </div>
            </div>
          )}
        </Card>
      </ContentWrapper>

      <ConfirmModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={async () => {
          try {
            setCancelling(true); setError(null);
            await apiService.post(`/api/student/exams/${examId}/cancel`, {});
            setToast('Exam cancelled');
            setTimeout(() => {
              setToast(null);
              navigate('/portal/student/exams');
            }, 1500);
          } catch (e: any) { setError(e?.message || 'Failed to cancel exam'); }
          finally { setCancelling(false); }
        }}
        title={t('portal.student.exams.cancel_title', 'Cancel Exam') as string}
        message={t('portal.student.exams.cancel_confirm', 'Are you sure you want to cancel this exam?') as string}
        confirmText={t('portal.common.yes_cancel', 'Yes, Cancel') as string}
        variant="danger"
      />
    </PageWrapper>
  );
};

export default StudentExamDetail;
